import loggerFactory from '../logger';
import { Client as StompClient } from '@stomp/stompjs';
import messageBus from '../message-bus';
import { x } from '../utils';
import defaultConfig from './default-config';

const log = loggerFactory.get('socket-enabler:client', {
    logger: loggerFactory.loggerTypes.CONSOLE,
});
let clientId = 1;

export default function Client() {
    const id = clientId++;
    let sockJS;
    let ready = false;
    let connectionAttempts;
    let stompClient;
    let onConnectCallbacks = [];
    let onReceiveCallbacks = [];
    let onErrorCallbacks = [];
    let onReadyCallbacks = [];
    let onConnectingCallbacks = [];
    let onCloseCallbacks = [];
    let onWSConnectionFailureCallbacks = [];
    let onSubscribeCallbacks = [];
    let config;
    let connected = false;
    let lastConnectionSuccessful;

    function setConnected(value) {
        connected = value;
        log.debug(`socket-enabler connected = ${value}`);
        messageBus.publish('socket-enabler', {
            id: id,
            connected: value,
        });
    }

    function onConnect() {
        ready = false;
        lastConnectionSuccessful = true;
        setConnected(true);
        connectionAttempts = 0;
        onConnectCallbacks.forEach(callback => callback());
    }

    function onReceive(message) {
        if (message && message.headers && message.headers.type === 'READY') {
            ready = true;
            onReadyCallbacks.forEach(callback => callback());
        }
        onReceiveCallbacks.forEach(callback => callback(message));
    }

    function onError(message) {
        log.error((message.headers && message.headers.message) || message);
        onErrorCallbacks.forEach(callback => callback(message));
        ready = false;
        stompClient.forceDisconnect(); // After forcing disconnect, automatic reconnect will be attempted.
        sockJS && sockJS.close();
        setConnected(false);
    }

    // Exponential back-off delay generation with random interval.
    function generateDelay(attempts) {
        let maxInterval = (Math.pow(2, attempts) - 1) * 1000;

        if (maxInterval > 30 * 1000) {
            maxInterval = 30 * 1000;
        }

        return Math.random() * maxInterval;
    }

    function beforeConnect() {
        return new Promise(resolve => {
            const delay = generateDelay(connectionAttempts);
            connectionAttempts++;
            setTimeout(resolve, delay);
        });
    }

    function getLogger(name) {
        const { CONSOLE } = loggerFactory.loggerTypes;
        return loggerFactory.get(name, { logger: CONSOLE }).debug;
    }

    async function connect() {
        log.debug('Connecting');
        onConnectingCallbacks.forEach(callback => callback());

        // Create a new SockJS instance
        stompClient = createStompClient();

        // If running on a WebSocket enabled browser
        if (typeof WebSocket === 'function') {
            // Generate BrokerUrl and add it to the stomp client
            stompClient.brokerURL = generateBrokerUrl();
        }
        // Fallback to SockJs when running on browsers that do not support WebSockets
        else {
            // Create new sockjs instance on each reconnect https://stomp-js.github.io/guide/stompjs/rx-stomp/ng2-stompjs/using-stomp-with-sockjs.html
            const sockJsInstance = await createWebSocket();
            stompClient.webSocketFactory = () => sockJsInstance;
        }

        stompClient.activate();
    }

    async function createWebSocket() {
        const { default: SockJS } = await import('sockjs-client');
        SockJS.prototype._debug = getLogger('sockjs');
        return new SockJS(config.url);
    }

    /**
     * Constructs WebSocket brokerUrl
     * STOMP clients communicate with any STOMP message broker using WebSocket connections.
     * The brokerUrl specifies the WebSocket URL to connect to the broker.
     *
     * @returns {string|undefined} brokerUrl
     */
    function generateBrokerUrl() {
        if (!config.baseUrl) {
            return;
        }

        const url = new URL(`${config.baseUrl}${config.destinationPrefix}`);

        // Check if the URL starts with "http" or "https"
        if (!url.protocol.startsWith('http')) {
            throw new Error('Invalid URL format. Please provide a valid HTTP or HTTPS URL.');
        }

        // Construct WebSocket URL
        const webSocketProtocol = url.protocol.replace('http', 'ws');

        return `${webSocketProtocol}//${url.host}${url.pathname}/websocket`;
    }

    function createStompClient() {
        return new StompClient({
            debug: getLogger('stompjs'),
            connectHeaders: { ...config.connectHeaders },
            heartbeatIncoming: config.stomp.heartbeat.incoming,
            heartbeatOutgoing: config.stomp.heartbeat.outgoing,
            reconnectDelay: 1, // Instantly reconnect. Delay will be handled in beforeConnect to support exponential back-off.
            beforeConnect: beforeConnect,
            onUnhandledMessage: onReceive,
            onConnect: onConnect,
            onStompError: onError,
            onWebSocketClose: onWebSocketClose,
        });
    }

    function init(_config) {
        config = x.extend({}, defaultConfig, _config);
        config.url = config.url || (config.baseUrl || '') + config.destinationPrefix;

        connectionAttempts = 0;
        onConnectCallbacks = [];
        onReceiveCallbacks = [];
        onErrorCallbacks = [];
        onReadyCallbacks = [];
        onConnectingCallbacks = [];
        onCloseCallbacks = [];
        onWSConnectionFailureCallbacks = [];
        onSubscribeCallbacks = [];
    }

    function subscribe(itemId, destination, versionTag, headerLocale, dataId, name, silent = false) {
        if (!connected) {
            return;
        }
        log.debug(`Subscribing to destination "${destination}" with versionTag ${versionTag}`);

        const headers = { id: itemId };
        if (versionTag) {
            headers.versionTag = versionTag;
        }
        if (headerLocale) {
            headers.locale = headerLocale;
        }
        !silent && onSubscribeCallbacks.forEach(callback => callback({ itemId: dataId, collection: name }))
        stompClient.subscribe(destination, onReceive, headers);
    }

    function subscribeMultiple(id, destination, headerLocale, itemIds, key, name) {
        if (!connected) {
            return;
        }
        log.debug(`Subscribing to destination "${destination}"`);

        const headers = { id: id };
        if (headerLocale) {
            headers.locale = headerLocale;
        }
        if (itemIds) {
            headers.mid = itemIds;
        }
        if (key) {
            headers.key = key;
        }
        onSubscribeCallbacks.forEach(callback => callback({ itemId: itemIds, collection: name}));
        stompClient.subscribe(destination, onReceive, headers);
    }

    function unsubscribe(itemId) {
        if (!connected || !stompClient.connected) {
            return;
        }
        log.debug(`Unsubscribing to id "${itemId}"`);
        stompClient.unsubscribe(itemId);
    }

    function send(itemId, destination, headerLocale, headers, body) {
        if (!connected) {
            return;
        }

        log.debug(`Send to ${destination}`, headers);

        headers = headers || {};
        headers.id = itemId;
        headers.destination = destination;
        headers.locale = headerLocale;

        return stompClient.publish({ destination, headers, body: body || '' });
    }

    async function onWebSocketClose(event) {
        onCloseCallbacks.forEach(callback => callback(event));

        if (event.wasClean === false ) {
            // Use SockJs fallback if initial connection using WebSockets fails
            if (!stompClient.webSocketFactory && !lastConnectionSuccessful) {
                const sockJsInstance = await createWebSocket();
                stompClient.webSocketFactory = () => sockJsInstance;
                onWSConnectionFailureCallbacks.forEach(callback => callback(event));
            }

            lastConnectionSuccessful = false;
        }
    }

    // Expose methods
    this.init = init;

    this.isConnected = () => connected;

    this.isReady = () => ready;

    this.connect = callback => {
        callback && onConnectCallbacks.push(callback);
        connect();
    };

    this.disconnect = () => {
        return stompClient
            .deactivate()
            .then(() => {
                setConnected(false);
            })
            .catch(error => log.debug('Failed to disconnect:', error));
    };

    this.onReceive = callback => onReceiveCallbacks.push(callback);
    this.onConnect = callback => onConnectCallbacks.push(callback);
    this.onError = callback => onErrorCallbacks.push(callback);
    this.onWSConnectionFailure = callback => onWSConnectionFailureCallbacks.push(callback);
    this.onReady = callback => onReadyCallbacks.push(callback);
    this.onConnecting = callback => onConnectingCallbacks.push(callback);
    this.onClose = callback => onCloseCallbacks.push(callback);
    this.onSubscribe = callback => onSubscribeCallbacks.push(callback);
    this.subscribe = subscribe;
    this.subscribeMultiple = subscribeMultiple;
    this.unsubscribe = unsubscribe;
    this.send = send;

    // Used for tests only
    this._getStompClient = () => stompClient;
    this._beforeConnect = beforeConnect;
    this._generateDelay = generateDelay;
}
